import { useIsomorphicLayoutEffect } from '@react-hookz/web';
import { useQueryClient } from '@tanstack/react-query';

import { useAuth0User } from '@hooks/useAuth0User';
import { setIsDemoApiSession } from '@lib/api/ffh-client';
import { isDemoUser } from '@lib/auth0/utils';

/**
 * Routes the session to the demo backend when the logged-in user carries the
 * app-review demo claim (see NEXT_PUBLIC_FFH_DEMO_API_URL).
 *
 * Runs in a layout effect so the routing flag is flushed before passive
 * effects fire their first backend requests. The Auth0 user still hydrates
 * asynchronously, so a demo session can briefly point at the standard
 * backend; whenever the flag flips, queries are reset (preserving mutation
 * state) so anything fetched against the wrong backend is refetched.
 */
export const useDemoApiRouting = (): void => {
  const { user } = useAuth0User();
  const queryClient = useQueryClient();
  const isDemoSession = isDemoUser(user);

  useIsomorphicLayoutEffect(() => {
    const didChange = setIsDemoApiSession(isDemoSession);
    if (didChange) {
      void queryClient.resetQueries();
    }
  }, [isDemoSession, queryClient]);
};
